import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  InputAdornment,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider
} from '@mui/material';
import {
  Search,
  Download,
  Refresh,
  CheckCircle,
  Cancel,
  Schedule,
  Today,
  Visibility,
  Edit,
  Delete,
  BeachAccess
} from '@mui/icons-material';
import { Scrollbar } from 'src/components/scrollbar';
import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { 
  Permission, 
  getUserPermissions, 
  getLeaveRequestsForUser,
  updatePermissionStatus,
  exportLeaveRequests,
  downloadFile,
  LeaveApprovalDto,
  getLeaveRequestsByBranch
} from 'src/api/permissionApi';
import { PermissionType } from 'src/sections/permission/enum/PermissionType';
import { PermissionStatus } from 'src/sections/permission/enum/PermissionStatus';
import { AllUsers, type EmployeeDto } from 'src/api/api_data';
import { getUserIdFromToken } from 'src/utils/jwt';
import { usersApi } from 'src/api/users.api';

interface BranchLeaveManagementTabProps {
  branchId: string;
  branchName: string;
}

// Leave type options with labels
const LEAVE_TYPE_OPTIONS = [
  { value: '', label: 'Tüm İzin Türleri' },
  { value: PermissionType.ANNUAL, label: 'Yıllık İzin' },
  { value: PermissionType.SICK, label: 'Hastalık İzni' },
  { value: PermissionType.MATERNITY, label: 'Doğum İzni' },
  { value: PermissionType.PATERNITY, label: 'Babalık İzni' },
  { value: PermissionType.MARRIAGE, label: 'Evlilik İzni' },
  { value: PermissionType.BEREAVEMENT, label: 'Ölüm İzni' },
  { value: PermissionType.UNPAID, label: 'Ücretsiz İzin' },
  { value: PermissionType.OTHER, label: 'Diğer' },
];

// Status options with labels
const STATUS_OPTIONS = [
  { value: '', label: 'Tüm Durumlar' },
  { value: PermissionStatus.PENDING, label: 'Beklemede' },
  { value: PermissionStatus.APPROVED, label: 'Onaylandı' },
  { value: PermissionStatus.REJECTED, label: 'Reddedildi' },
  { value: PermissionStatus.CANCELLED, label: 'İptal Edildi' },
];

const BranchLeaveManagementTab: React.FC<BranchLeaveManagementTabProps> = ({ branchId, branchName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<Permission[]>([]);
  const [employees, setEmployees] = useState<EmployeeDto[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter states
  // Varsayılan: tarih filtresi YOK (tüm izinler görünsün); kullanıcı isterse daraltır.
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchEmployee, setSearchEmployee] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('');

  // Action dialog state
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    permission: Permission | null;
    action: 'approve' | 'reject' | null;
  }>({
    open: false,
    permission: null,
    action: null
  });

  // Details dialog state
  const [detailsDialog, setDetailsDialog] = useState<{
    open: boolean;
    permission: Permission | null;
  }>({
    open: false,
    permission: null
  });

  // Leave summary stats
  const [stats, setStats] = useState({
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    rejectedRequests: 0
  });

  // Fetch employees for branch
  const fetchEmployees = useCallback(async () => {
    try {
      const userId = getUserIdFromToken();
      if (!userId) return;
      
      const response = await usersApi.getEmployeesForBranch(branchId, page, rowsPerPage);
      const employeeData = response.content as any || [];


      // Filter employees for this branch
      const branchEmployees = employeeData.filter((emp: EmployeeDto) => 
        emp.branchName && emp.branchName.toLowerCase().includes(branchName.toLowerCase())
      );
      
      setEmployees(branchEmployees);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  }, [branchId, branchName, page, rowsPerPage]);



  // Fetch leave requests
  const fetchLeaveRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userId = getUserIdFromToken();
      if (!userId) {
        throw new Error('Kullanıcı bilgisi bulunamadı');
      }

      let requests: Permission[] = [];
      
      try {
        // Try to fetch from new API with pagination
        const response = await getLeaveRequestsByBranch(branchId, 0, 1000);
        // Backend /by-branch zaten şubeye göre scope'lar (silinmiş çalışanlar dahil).
        // FE'de paginated/silinmiş-hariç çalışan listesiyle TEKRAR filtrelemiyoruz — izinleri düşürüyordu.
        requests = response.content;
      } catch (apiError) {
        console.warn('New API failed, trying old API:', apiError);
        
        try {
          // Fallback to old API
          requests = await getUserPermissions(userId);
          
          // Filter by branch employees
          const branchEmployeeIds = employees.map(emp => emp.id);
          requests = requests.filter(req => branchEmployeeIds.includes(req.employee?.id));
        } catch (oldApiError) {
          console.error('All leave request APIs failed:', oldApiError);
          throw new Error('İzin talepleri alınamadı. Lütfen daha sonra tekrar deneyin.');
        }
      }

      // Apply filters
      let filteredRequests = requests;

      // Date filter
      if (startDate && endDate) {
        filteredRequests = filteredRequests.filter(req => {
          const reqStartDate = new Date(req.startDate);
          const filterStartDate = new Date(startDate);
          const filterEndDate = new Date(endDate);
          return reqStartDate >= filterStartDate && reqStartDate <= filterEndDate;
        });
      }

      // Employee search filter
      if (searchEmployee) {
        filteredRequests = filteredRequests.filter(req => {
          const employeeName = `${req.employee?.firstName} ${req.employee?.lastName}`.toLowerCase();
          const employeeEmail = req.employee?.email?.toLowerCase() || '';
          const search = searchEmployee.toLowerCase();
          return employeeName.includes(search) || employeeEmail.includes(search);
        });
      }

      // Status filter
      if (statusFilter) {
        filteredRequests = filteredRequests.filter(req => req.status === statusFilter);
      }

      // Leave type filter
      if (leaveTypeFilter) {
        filteredRequests = filteredRequests.filter(req => req.leaveType === leaveTypeFilter);
      }

      setLeaveRequests(filteredRequests);

      // Calculate stats
      const newStats = {
        totalRequests: requests.length,
        pendingRequests: requests.filter(req => req.status === PermissionStatus.PENDING).length,
        approvedRequests: requests.filter(req => req.status === PermissionStatus.APPROVED).length,
        rejectedRequests: requests.filter(req => req.status === PermissionStatus.REJECTED).length
      };
      setStats(newStats);

    } catch (err: any) {
      setError(err.message || 'İzin talepleri yüklenirken bir hata oluştu');
      setLeaveRequests([]);
    } finally {
      setLoading(false);
    }
  }, [employees, startDate, endDate, searchEmployee, statusFilter, leaveTypeFilter, branchId]);

  // Initial data load
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    if (employees.length > 0) {
      fetchLeaveRequests();
    }
  }, [fetchLeaveRequests, employees]);

  // Event handlers
  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchEmployee(event.target.value);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  const handleLeaveTypeChange = (event: SelectChangeEvent) => {
    setLeaveTypeFilter(event.target.value);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchLeaveRequests();
  };

  // Action handlers
  const handleActionClick = (permission: Permission, action: 'approve' | 'reject') => {
    setActionDialog({
      open: true,
      permission,
      action
    });
  };

  const handleActionConfirm = async () => {
    if (!actionDialog.permission || !actionDialog.action) return;

    try {
      setLoading(true);
      
      const approvalData: LeaveApprovalDto = {
        status: actionDialog.action === 'approve' ? PermissionStatus.APPROVED : PermissionStatus.REJECTED,
        approvedBy: getUserIdFromToken() || undefined
      };

      await updatePermissionStatus(actionDialog.permission.id, approvalData);
      
      // Refresh the list
      fetchLeaveRequests();
      
      setActionDialog({ open: false, permission: null, action: null });
    } catch (err: any) {
      setError(err.message || 'İşlem sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleActionCancel = () => {
    setActionDialog({ open: false, permission: null, action: null });
  };

  // Details dialog handlers
  const handleViewDetails = (permission: Permission) => {
    setDetailsDialog({
      open: true,
      permission
    });
  };

  const handleDetailsClose = () => {
    setDetailsDialog({ open: false, permission: null });
  };

  const handleExport = async () => {
    try {
      const exportParams = {
        format: 'EXCEL' as const,
        startDate,
        endDate,
        branchId,
        fileName: `izin-talepleri-${branchName}-${format(new Date(), 'yyyy-MM-dd')}`
      };

      const blob = await exportLeaveRequests(exportParams);
      downloadFile(blob, exportParams.fileName, 'EXCEL');
    } catch (err: any) {
      setError(err.message || 'Export işlemi sırasında bir hata oluştu');
    }
  };

  // Helper functions
  const getStatusColor = (status: PermissionStatus) => {
    switch (status) {
      case PermissionStatus.APPROVED: return 'success';
      case PermissionStatus.PENDING: return 'warning';
      case PermissionStatus.REJECTED: return 'error';
      case PermissionStatus.CANCELLED: return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: PermissionStatus) => {
    switch (status) {
      case PermissionStatus.APPROVED: return <CheckCircle />;
      case PermissionStatus.PENDING: return <Schedule />;
      case PermissionStatus.REJECTED: return <Cancel />;
      case PermissionStatus.CANCELLED: return <Cancel />;
      default: return <Today />;
    }
  };

  const getStatusLabel = (status: PermissionStatus) => {
    const statusObj = STATUS_OPTIONS.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getLeaveTypeLabel = (leaveType: PermissionType) => {
    const typeObj = LEAVE_TYPE_OPTIONS.find(t => t.value === leaveType);
    return typeObj ? typeObj.label : leaveType;
  };

  const getLeaveTypeColor = (leaveType: PermissionType) => {
    switch (leaveType) {
      case PermissionType.ANNUAL: return 'primary';
      case PermissionType.SICK: return 'error';
      case PermissionType.MATERNITY: return 'secondary';
      case PermissionType.PATERNITY: return 'info';
      case PermissionType.MARRIAGE: return 'success';
      case PermissionType.BEREAVEMENT: return 'default';
      case PermissionType.UNPAID: return 'warning';
      default: return 'default';
    }
  };

  const calculateLeaveDays = (leaveStartDate: string, leaveEndDate: string) => {
    const start = new Date(leaveStartDate);
    const end = new Date(leaveEndDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Pagination
  const paginatedRequests = leaveRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          İzin talepleri yükleniyor...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {branchName} - İzin Yönetimi
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={leaveRequests.length === 0}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              Yenile
            </Button>
          </Stack>
        </Stack>

        {/* Stats Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <BeachAccess />
                  </Avatar>
                  <Stack>
                    <Typography variant="h4">
                      {stats.totalRequests}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Talep
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    <Schedule />
                  </Avatar>
                  <Stack>
                    <Typography variant="h4">
                      {stats.pendingRequests}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Beklemede
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <CheckCircle />
                  </Avatar>
                  <Stack>
                    <Typography variant="h4">
                      {stats.approvedRequests}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Onaylandı
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'error.main' }}>
                    <Cancel />
                  </Avatar>
                  <Stack>
                    <Typography variant="h4">
                      {stats.rejectedRequests}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reddedildi
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card>
          <CardHeader title="Filtreleme" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Başlangıç Tarihi"
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Bitiş Tarihi"
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Çalışan Ara"
                  value={searchEmployee}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Durum</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Durum"
                    onChange={handleStatusChange}
                  >
                    {STATUS_OPTIONS.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>İzin Türü</InputLabel>
                  <Select
                    value={leaveTypeFilter}
                    label="İzin Türü"
                    onChange={handleLeaveTypeChange}
                  >
                    {LEAVE_TYPE_OPTIONS.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert severity="error">
            {error}
            <Button onClick={handleRefresh} sx={{ ml: 2 }}>
              Tekrar Dene
            </Button>
          </Alert>
        )}

        {/* Table */}
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Çalışan</TableCell>
                    <TableCell>İzin Türü</TableCell>
                    <TableCell>Başlangıç</TableCell>
                    <TableCell>Bitiş</TableCell>
                    <TableCell>Gün Sayısı</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>Sebep</TableCell>
                    <TableCell align="right">İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRequests.map((request) => (
                    <TableRow key={request.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {request.employee?.firstName?.charAt(0) || '?'}
                          </Avatar>
                          <Stack>
                            <Typography variant="subtitle2">
                              {`${request.employee?.firstName || ''} ${request.employee?.lastName || ''}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {request.employee?.email}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getLeaveTypeLabel(request.leaveType)}
                          color={getLeaveTypeColor(request.leaveType)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(request.startDate), 'dd/MM/yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(request.endDate), 'dd/MM/yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {calculateLeaveDays(request.startDate, request.endDate)} gün
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Label 
                          variant="soft" 
                          color={getStatusColor(request.status)}
                          startIcon={getStatusIcon(request.status)}
                        >
                          {getStatusLabel(request.status)}
                        </Label>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {request.reason}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5}>
                          {request.status === PermissionStatus.PENDING && (
                            <>
                              <Tooltip title="Onayla">
                                <IconButton 
                                  size="small" 
                                  color="success"
                                  onClick={() => handleActionClick(request, 'approve')}
                                >
                                  <CheckCircle />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reddet">
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleActionClick(request, 'reject')}
                                >
                                  <Cancel />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Detaylar">
                            <IconButton 
                              size="small"
                              onClick={() => handleViewDetails(request)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Seçilen kriterlere uygun izin talebi bulunamadı
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            component="div"
            count={leaveRequests.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Sayfa başına satır:"
          />
        </Card>

        {/* Action Confirmation Dialog */}
        <Dialog
          open={actionDialog.open}
          onClose={handleActionCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            İzin Talebi {actionDialog.action === 'approve' ? 'Onayı' : 'Reddi'}
          </DialogTitle>
          <DialogContent>
            {actionDialog.permission && (
              <Stack spacing={2}>
                <Typography>
                  <strong>Çalışan:</strong> {`${actionDialog.permission.employee?.firstName} ${actionDialog.permission.employee?.lastName}`}
                </Typography>
                <Typography>
                  <strong>İzin Türü:</strong> {getLeaveTypeLabel(actionDialog.permission.leaveType)}
                </Typography>
                <Typography>
                  <strong>Tarih:</strong> {format(new Date(actionDialog.permission.startDate), 'dd/MM/yyyy')} - {format(new Date(actionDialog.permission.endDate), 'dd/MM/yyyy')}
                </Typography>
                <Typography>
                  <strong>Sebep:</strong> {actionDialog.permission.reason}
                </Typography>
                <Divider />
                <Typography color="text.secondary">
                  Bu izin talebini {actionDialog.action === 'approve' ? 'onaylamak' : 'reddetmek'} istediğinizden emin misiniz?
                </Typography>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleActionCancel}>
              İptal
            </Button>
            <Button 
              onClick={handleActionConfirm} 
              variant="contained"
              color={actionDialog.action === 'approve' ? 'success' : 'error'}
            >
              {actionDialog.action === 'approve' ? 'Onayla' : 'Reddet'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Leave Details Dialog */}
        <Dialog
          open={detailsDialog.open}
          onClose={handleDetailsClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            İzin Talebi Detayları
          </DialogTitle>
          <DialogContent>
            {detailsDialog.permission && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">Çalışan</Typography>
                  <Typography>
                    {`${detailsDialog.permission.employee?.firstName} ${detailsDialog.permission.employee?.lastName}`}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">E-posta</Typography>
                  <Typography>
                    {detailsDialog.permission.employee?.email || '-'}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">İzin Türü</Typography>
                  <Chip 
                    label={getLeaveTypeLabel(detailsDialog.permission.leaveType)}
                    color={getLeaveTypeColor(detailsDialog.permission.leaveType) as any}
                    size="small"
                  />
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">Durum</Typography>
                  <Label color={getStatusColor(detailsDialog.permission.status)}>
                    {getStatusLabel(detailsDialog.permission.status)}
                  </Label>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">Başlangıç Tarihi</Typography>
                  <Typography>
                    {format(new Date(detailsDialog.permission.startDate), 'dd/MM/yyyy')}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">Bitiş Tarihi</Typography>
                  <Typography>
                    {format(new Date(detailsDialog.permission.endDate), 'dd/MM/yyyy')}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">Toplam Gün</Typography>
                  <Typography fontWeight="bold">
                    {calculateLeaveDays(detailsDialog.permission.startDate, detailsDialog.permission.endDate)} gün
                  </Typography>
                </Stack>
                <Divider />
                <Stack>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Sebep</Typography>
                  <Typography sx={{ bgcolor: 'background.default', p: 1.5, borderRadius: 1 }}>
                    {detailsDialog.permission.reason || 'Belirtilmemiş'}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">Talep Tarihi</Typography>
                  <Typography variant="body2">
                    {format(new Date(detailsDialog.permission.createdAt), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
                {detailsDialog.permission.approvedAt && (
                  <>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" color="text.secondary">
                        {detailsDialog.permission.status === PermissionStatus.APPROVED ? 'Onay Tarihi' : 'Red Tarihi'}
                      </Typography>
                      <Typography variant="body2">
                        {format(new Date(detailsDialog.permission.approvedAt), 'dd/MM/yyyy HH:mm')}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDetailsClose} variant="contained">
              Kapat
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default BranchLeaveManagementTab;